import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";
import { allArticles } from "@/mocks/ranking";
import { ArticleCard, Tag } from "@/components/feature/cards";

export default function RankDetail() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === id);

  useSeo({
    title: article ? `${article.title} - 吃瓜榜单` : "内容不存在 - 吃瓜榜单",
    description: article ? article.excerpt : "未找到对应盘点内容。",
    keywords: article ? [...article.tags, "吃瓜榜单"].join(",") : "吃瓜榜单",
    canonicalPath: article ? `/rank/${article.id}` : "/",
    ogType: "article",
    jsonLdId: article ? `article-${article.id}` : "article-missing",
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.cover,
          datePublished: article.date,
          author: { "@type": "Organization", name: "吃瓜榜单" },
          publisher: { "@type": "Organization", name: "吃瓜榜单" },
          mainEntityOfPage: `https://chiguabangdan.example/rank/${article.id}`,
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://chiguabangdan.example/" },
              {
                "@type": "ListItem",
                position: 2,
                name: article.group === "annual" ? "年度盘点" : "月度总结",
                item: `https://chiguabangdan.example/${article.group === "annual" ? "annual" : "monthly"}`,
              },
              { "@type": "ListItem", position: 3, name: article.title, item: `https://chiguabangdan.example/rank/${article.id}` },
            ],
          },
        }
      : undefined,
  });

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-file-search-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-6 text-2xl font-black text-foreground-950">这条瓜还没被收录</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的盘点内容不存在或已被下线，去看看其他榜单吧。</p>
          <Link to="/" className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-semibold text-background-50 hover:bg-primary-600">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const backTo = article.group === "annual" ? "/annual" : "/monthly";
  const backLabel = article.group === "annual" ? "全部年度盘点" : "全部月度总结";
  const sameCat = allArticles.filter((a) => a.category === article.category && a.id !== article.id);
  const related = (sameCat.length >= 3 ? sameCat : allArticles.filter((a) => a.id !== article.id)).slice(0, 3);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to={backTo} className="hover:text-primary-600">{backLabel}</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="line-clamp-1 text-foreground-800">{article.title}</span>
        </nav>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          <article>
            <div className="flex flex-wrap items-center gap-2">
              <Tag label={article.category} />
              {article.tags.slice(0, 2).map((t) => (
                <Tag key={t} label={t} />
              ))}
            </div>
            <h1 className="mt-4 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">{article.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                发布于 {article.date}
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line" aria-hidden="true"></i>
                热度 {article.heat}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-medal-line" aria-hidden="true"></i>
                {article.group === "annual" ? "年度" : "月度"}榜 第 {article.rank} 名
              </span>
            </div>

            <div className="mt-7 overflow-hidden rounded-lg ring-1 ring-background-200/70">
              <img
                src={article.cover}
                alt={`${article.title}头图`}
                title={`${article.title} · 吃瓜榜单`}
                className="aspect-[16/9] w-full object-cover object-top"
              />
            </div>

            {article.standouts && article.standouts.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {article.standouts.map((s) => (
                  <span key={s} className="whitespace-nowrap rounded-md bg-accent-100 px-3 py-1.5 text-sm text-accent-800">
                    <i className="ri-star-fill mr-1" aria-hidden="true"></i>
                    {s}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-7 flex flex-col gap-6 border-t border-background-200/70 pt-7">
              {article.content.map((p, i) => (
                <p key={i} className="text-base leading-8 text-foreground-700">{p}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200/70 pt-6">
              {article.tags.map((t) => (
                <Link
                  key={t}
                  to={backTo}
                  className="whitespace-nowrap rounded-full bg-secondary-100 px-3.5 py-1.5 text-sm text-secondary-700 transition-colors hover:bg-secondary-200"
                >
                  # {t}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 rounded-lg bg-background-100/70 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-600">
                想看看这一栏的其他内容？
              </p>
              <Link
                to={backTo}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                返回{backLabel}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </article>

          {/* 侧栏 */}
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-primary-100/60 p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-flash-line text-primary-600" aria-hidden="true"></i>
                理性吃瓜提示
              </h2>
              <p className="mt-3 text-sm leading-6 text-foreground-700">
                本页内容为热点事件的信息整理与复盘，旨在帮助读者了解话题脉络。我们坚持先溯源、后盘点，对仍在发酵的事件会明确标注，请在全面了解后再作判断。
              </p>
            </div>

            <div>
              <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-stackshare-line text-primary-600" aria-hidden="true"></i>
                相关推荐
              </h2>
              <div className="flex flex-col gap-4">
                {related.map((r) => (
                  <ArticleCard key={r.id} article={r} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}